import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import { resetPasswordAsync } from "../features/authentication/authenticationApi";
import { HttpStatusCode } from "axios";
import { StringExtension } from "../features/extensions/StringExtension";
import { ROUTES } from "../routes";
import { useTranslation } from "react-i18next";
import Button from "../components/create-event/Button";
import PopupContainer from "../components/popup/PopupContainer";

const ResetPasswordEmailSentPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const stringExtension = new StringExtension();

  const [email, setEmail] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Prefer email from navigation state, fallback to localStorage
    if ((location.state as any)?.email) {
      const stateEmail = (location.state as any).email as string;
      setEmail(stateEmail);
      localStorage.setItem("pendingResetEmail", stateEmail);
    } else {
      const storedEmail = localStorage.getItem("pendingResetEmail");
      if (storedEmail) {
        setEmail(storedEmail);
      } else {
        navigate(ROUTES.LOGIN);
      }
    }
  }, [location.state, navigate]);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleOpenGmail = () => {
    window.open("https://mail.google.com", "_blank");
  };

  const handleOpenOutlook = () => {
    window.open("https://outlook.office.com/mail/", "_blank");
  };

  const handleResendEmail = async () => {
    setErrorMessage(null);
    if (stringExtension.isEmpty(email)) {
      setErrorMessage(t("auth.errors.emailRequired"));
      return;
    }
    if (!validateEmail(email)) {
      setErrorMessage(t("auth.errors.invalidEmailFormat"));
      return;
    }

    setIsResending(true);
    try {
      const resultAction = await dispatch(resetPasswordAsync(email));
      if (resetPasswordAsync.fulfilled.match(resultAction)) {
        setTimer(30);
        const countdown = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(countdown);
              setIsResending(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (resetPasswordAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setErrorMessage(t("auth.errors.invalidEmailFormat"));
              break;
            case HttpStatusCode.NotFound:
              setErrorMessage(t("auth.errors.emailNotRegistered"));
              break;
            case HttpStatusCode.InternalServerError:
              setErrorMessage(t("auth.errors.serverError"));
              break;
            default:
              setErrorMessage(
                errorResponse.message || t("auth.errors.unknownError")
              );
              break;
          }
        } else {
          setErrorMessage(t("auth.errors.unknownError"));
        }
        setIsResending(false);
      }
    } catch (_error) {
      setErrorMessage(t("auth.errors.unexpectedError"));
      setIsResending(false);
    }
  };

  return (
    <PopupContainer>
          <div className="flex justify-center mb-4">
            <svg
              className="w-30 h-30 text-primary-40 dark:!text-primary-90"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-center mb-4 dark:text-white">
            {t("resetPasswordSentPage.title")}
          </h1>

          <p className="text-center mb-2 dark:text-gray-200">
            {t("resetPasswordSentPage.message")}
          </p>
          <div className="text-center mb-4">
            <span className="bg-primary-40 dark:bg-primary-90 border border-primary-40 dark:border-primary-90 text-white dark:text-primary-20 px-3 py-1.5 rounded-full inline-block text-center font-medium">
              {email}
            </span>
          </div>

          <div className="flex justify-center gap-4 mb-4">
            <Button variant="secondary" onClick={handleOpenGmail}>
              <span className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  style={{ fill: "none" }}
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    style={{ fill: "#4285F4" }}
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    style={{ fill: "#34A853" }}
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    style={{ fill: "#FBBC05" }}
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    style={{ fill: "#EA4335" }}
                  />
                </svg>
                {t("emailConfirmation.openGmail")}
              </span>
            </Button>
            <Button variant="secondary" onClick={handleOpenOutlook}>
              <span className="flex items-center gap-2">
                <img src="/assets/images/icons/outlook-icon.svg" alt="Outlook" className="w-5 h-5" />
                {t("emailConfirmation.openOutlook")}
              </span>
            </Button>
          </div>

          {/* Email field removed per request; showing only actions */}

          {((isResending && timer > 0) || !!errorMessage) && (
            <div className="flex flex-col items-center gap-3 mb-3">
              {isResending && timer > 0 && (
                <span className="text-gray-500 dark:text-white">
                  {timer} {t("emailConfirmation.seconds")}
                </span>
              )}
              {errorMessage && (
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {errorMessage}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3 mb-4 w-full">
            <Button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full justify-center"
            >
              {t("resetPasswordSentPage.resendEmail")}
            </Button>
            <button
              className="btn btn-secondary w-full flex items-center justify-center gap-2"
              onClick={() => navigate(ROUTES.LOGIN)}
            >
              <span className="text-center">{t("auth.signIn")}</span>
            </button>
          </div>
    </PopupContainer>
  );
};

export default ResetPasswordEmailSentPage;
